import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatDialogRef,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { CertificateType } from '../../../../shared/models/certificate-type.enum';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  debounceTime,
  map,
  distinctUntilChanged,
  switchMap,
  catchError,
  of,
  shareReplay,
  startWith,
} from 'rxjs';
import { WebsitesService } from '../../../../core/services/websites.service';
import { OriginVhostService } from '../../../website-overview/services/origin-vhost.service';
import { AsyncPipe } from '@angular/common';

interface SelectOption<T> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-install-ssl-certificate-global-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    MatCheckboxModule,
    AsyncPipe,
  ],
  templateUrl: './install-ssl-certificate-global-dialog.component.html',
  styleUrl: './install-ssl-certificate-global-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallSslCertificateGlobalDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<InstallSslCertificateGlobalDialogComponent>);

  private readonly formBuilder = inject(FormBuilder);

  private readonly websitesService = inject(WebsitesService);

  private readonly originVhostService = inject(OriginVhostService);

  private readonly destroyRef = inject(DestroyRef);

  readonly domainSearchText = signal('');

  private readonly domainSearchText$ = toObservable(this.domainSearchText);

  readonly aliasOptions = signal<SelectOption<string>[]>([]);

  readonly certificateTypeOptions: SelectOption<string>[] = [
    {
      value: CertificateType.LetsEncrypt,
      label: 'LetsEncrypt',
    },
    {
      value: CertificateType.SelfSigned,
      label: 'Self-Signed',
    },
  ];

  readonly form = this.formBuilder.group({
    type: this.formBuilder.nonNullable.control(CertificateType.LetsEncrypt, Validators.required),

    websiteId: this.formBuilder.control<string | null>(null, Validators.required),

    subDomains: this.formBuilder.nonNullable.control<string[]>([]),

    vhostAliases: this.formBuilder.nonNullable.control(true),

    autoRenew: this.formBuilder.nonNullable.control(true),

    altNames: this.formBuilder.nonNullable.control<string[]>({
      value: [],
      disabled: true,
    }),
  });

  get typeHint(): string {
    switch (this.form.controls.type.value) {
      case CertificateType.LetsEncrypt:
        return 'Let’s Encrypt is a free, automated, open certificate authority used to generate SSL certificates to be used by VirtualMax.';

      case CertificateType.SelfSigned:
        return 'A self-signed certificate is a locally generated SSL certificate for VMAX. It provides encrypted connections but is not trusted by browsers by default and may trigger security warnings.';

      default:
        return '';
    }
  }

  readonly websiteOptions$ = this.domainSearchText$.pipe(
    debounceTime(300),
    map((searchText) => searchText.trim()),
    distinctUntilChanged(),
    switchMap((domainSearch) =>
      this.websitesService.searchWebsites({
        page: 1,
        pageSize: 20,
        domainSearch: domainSearch || undefined,
      }),
    ),
    map(({ Websites }) =>
      Websites.map((website) => ({
        label: website.domain,
        value: String(website.id),
      })),
    ),
    catchError(() => of([])),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  private listenForWebsiteChanges(): void {
    this.form.controls.websiteId.valueChanges
      .pipe(
        distinctUntilChanged(),
        switchMap((websiteId) => {
          if (!websiteId) {
            return of([]);
          }

          return this.originVhostService.getVhostByWebsiteId(Number(websiteId)).pipe(
            map((vhost) => vhost.aliases),
            catchError(() => of([])),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((aliases) => {
        const options = aliases.map((alias) => ({
          label: alias.alias,
          value: alias.alias,
        }));

        this.aliasOptions.set(options);
        this.updateAliases(this.form.controls.vhostAliases.value);
      });
  }

  private listenForVhostAliasesChanges(): void {
    this.form.controls.vhostAliases.valueChanges
      .pipe(startWith(this.form.controls.vhostAliases.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((selectAllAliases) => {
        this.updateAliases(selectAllAliases);
      });
  }

  private updateAliases(selectAllAliases: boolean): void {
    const altNamesControl = this.form.controls.altNames;

    if (selectAllAliases) {
      altNamesControl.setValue(this.aliasOptions().map((option) => option.value));

      altNamesControl.disable();
    } else {
      altNamesControl.enable();
    }

    altNamesControl.updateValueAndValidity();
  }

  ngOnInit(): void {
    this.listenForWebsiteChanges();
    this.listenForVhostAliasesChanges();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();

    if (value.type === CertificateType.LetsEncrypt) {
      this.form.controls.subDomains.setValue(value.altNames);
      this.form.controls.altNames.reset();
    }

    const rawValue = this.form.getRawValue();

    const filteredValue = Object.fromEntries(
      Object.entries(rawValue).filter(([, fieldValue]) => {
        if (fieldValue == null) return false;
        if (typeof fieldValue === 'string') return fieldValue.trim() !== '';
        if (Array.isArray(fieldValue)) return fieldValue.length > 0;

        return true;
      }),
    );

    this.dialogRef.close({
      ...filteredValue,
      websiteId: Number(rawValue.websiteId),
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
